/**
 * Should the Spine's "Run complete · N/M drafted" banner be showing?
 *
 * Pure predicate, extracted from VariantCShell so the rule is testable
 * without mounting the shell.
 *
 * # DECISION: ``post-run`` alone does not mean "nothing is running".
 * # WHY: a regen re-drafts a section on the SAME run_id, and sseAdapter's
 * #      ``regen_started`` deliberately does NOT emit RUN_STARTED ("the
 * #      reducer stays in ``post-run``"), while ``regen_complete`` dispatches
 * #      nothing at all. So for the entire regen the reducer still reads
 * #      ``post-run`` and the banner claimed the run was finished while a
 * #      section was visibly drafting next to it (hot-orange node + "Run
 * #      complete" in the same viewport).
 * # HOW:  ask the spine, which does know: any row drafting ⇒ work in flight.
 * # DATE: 2026-07-17
 *
 * Not cosmetic: the banner hosts "Load doc" (pulls the drafted document into
 * Word — mid-regen that means a half-rewritten section) and "Draft more
 * sections" (starts a run while one is live), so it stands down as a unit.
 */
export function shouldShowPostRunBanner(params: {
  /** Banner is Spine-only; the Chat view never shows it. */
  isSpineView: boolean;
  /** Reducer lifecycle state (``state.state``). */
  runState: string;
  /** Reducer has a RunSummary to render counts from. */
  hasRunSummary: boolean;
  /** Spine rows — only ``status`` is read. */
  sections: readonly { status: string }[];
}): boolean {
  const { isSpineView, runState, hasRunSummary, sections } = params;
  if (!isSpineView || runState !== "post-run" || !hasRunSummary) return false;
  return !sections.some((s) => s.status === "drafting");
}
